import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Loader2, AlertCircle, AlertTriangle } from 'lucide-react';

const ADMIN_LOGO_URL = 'https://customer-assets.emergentagent.com/job_8c0fe4ee-111e-41b1-aff2-64466f27b787/artifacts/dwo3qstc_Stock%20%26%20Load%20Admin.png';

export const AdminLogin = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(true);
  const [adminConfigured, setAdminConfigured] = useState(true);
  const [statusMessage, setStatusMessage] = useState('');
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    // Check if already logged in
    const token = localStorage.getItem('admin_token');
    if (token) {
      navigate('/admin/dashboard');
      return;
    }
    
    // Check if admin is configured
    const checkAdminStatus = async () => {
      const maxRetries = 3;
      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          const response = await adminAPI.getStatus();
          setAdminConfigured(response.data.configured);
          setStatusMessage(response.data.message);
          setCheckingStatus(false);
          return;
        } catch (err) {
          // Retry after delay (Cloudflare challenge may need time)
          if (attempt < maxRetries) {
            await new Promise(r => setTimeout(r, 2000 * attempt));
          } else {
            setAdminConfigured(false);
            setStatusMessage('Admin not configured. Set ADMIN_EMAIL and ADMIN_PASSWORD environment variables.');
            setCheckingStatus(false);
          }
        }
      }
    };
    
    checkAdminStatus();
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await adminAPI.login(email, password);
      localStorage.setItem('admin_token', response.data.access_token);
      navigate('/admin/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  if (checkingStatus) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 to-slate-800 p-4">
        <Loader2 className="w-8 h-8 animate-spin text-white" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 to-slate-800 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-2">
            {adminConfigured ? (
              <img src={ADMIN_LOGO_URL} alt="Stock & Load Admin" className="h-24 w-auto object-contain" data-testid="admin-logo" />
            ) : (
              <div className="w-16 h-16 rounded-full flex items-center justify-center bg-amber-100">
                <AlertTriangle className="w-8 h-8 text-amber-600" />
              </div>
            )}
          </div>
          <CardTitle className="text-2xl font-bold">Admin Portal</CardTitle>
        </CardHeader>
        <CardContent>
          {!adminConfigured ? (
            <div className="space-y-4">
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-amber-800">Admin Not Configured</p>
                    <p className="text-sm text-amber-700 mt-1">{statusMessage}</p>
                  </div>
                </div>
              </div>
              <div className="p-4 bg-slate-50 border rounded-lg">
                <p className="text-sm font-medium text-slate-700 mb-2">To enable admin access:</p>
                <ol className="text-sm text-slate-600 space-y-1 list-decimal list-inside">
                  <li>Set <code className="bg-slate-200 px-1 rounded">ADMIN_EMAIL</code> environment variable</li>
                  <li>Set <code className="bg-slate-200 px-1 rounded">ADMIN_PASSWORD</code> environment variable</li>
                  <li>Restart the backend service</li>
                </ol>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                  <AlertCircle className="w-4 h-4" />
                  {error}
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@example.com"
                  required
                  className="h-12"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="h-12"
                />
              </div>
              
              <Button type="submit" className="w-full h-12" disabled={loading}>
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
